/**
 * Transfers from an account that is not the client's registered one (S-27).
 *
 * The money is never credited; it goes back to the account it came from (two managers, in the
 * returns queue). What lives here is the compliance side: the screening of the sender's name and
 * the 24-hour suspicion-report decision, which returning the money does not discharge. Only the
 * designated compliance officer and super_admins can open this page; nothing of it reaches the
 * client or the Account Executive.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import transferCasesService, {
  type AvisoDecision,
  type TransferCase,
} from '../../services/transferCases.service';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import { formatCents } from '../../lib/amount';

/** The report is due within 24 hours of the suspicion. */
const DUE_HOURS = 24;
const MIN_NOTE = 10;

function CaseCard({
  item,
  onDone,
}: Readonly<{ item: TransferCase; onDone: () => void }>): JSX.Element {
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decided = item.aviso_decision !== 'not_assessed';
  const overdue = !decided && item.hours_open >= DUE_HOURS;
  const ready = note.trim().length >= MIN_NOTE && isMfaCode(code.trim());

  const decide = async (decision: AvisoDecision): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await transferCasesService.decideAviso(item.id, decision, note.trim(), code.trim());
      onDone();
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            {item.currency_code} {formatCents(item.amount)} ·{' '}
            <span translate="no">{item.sender_name ?? '—'}</span>
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Opened {formatDateTime(item.opened_at)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {item.sanctions_outcome === 'potential_match' && (
            <StatusBadge tone="danger">Possible match on a list</StatusBadge>
          )}
          {overdue && <StatusBadge tone="danger">Decision overdue</StatusBadge>}
          {decided && (
            <StatusBadge tone="neutral">
              {item.aviso_decision === 'filed' ? 'Report filed' : 'Report not filed'}
            </StatusBadge>
          )}
        </div>
      </div>

      {decided ? (
        <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>
          {item.aviso_note}
        </p>
      ) : (
        <>
          <label
            className="block text-xs mt-4 mb-1"
            htmlFor={`aviso-note-${item.id}`}
            style={{ color: colors.muted }}
          >
            The reason for the decision
          </label>
          <textarea
            id={`aviso-note-${item.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
          <div className="flex flex-wrap items-end gap-3 mt-3">
            <div>
              <label
                className="block text-xs mb-1"
                htmlFor={`aviso-code-${item.id}`}
                style={{ color: colors.muted }}
              >
                Authentication code
              </label>
              <input
                id={`aviso-code-${item.id}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(cleanMfaInput(e.target.value))}
                className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
                style={{ border: `1px solid ${colors.border}` }}
              />
            </div>
            <button
              type="button"
              disabled={busy || !ready}
              onClick={() => { void decide('filed'); }}
              className="text-sm px-3 py-2 rounded-lg font-semibold"
              style={{ background: colors.gold, color: colors.navy }}
            >
              Report filed
            </button>
            <button
              type="button"
              disabled={busy || !ready}
              onClick={() => { void decide('not_filed'); }}
              className="text-sm px-3 py-2 rounded-lg font-semibold"
              style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
            >
              Not filed
            </button>
          </div>
        </>
      )}
      {error && (
        <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
    </li>
  );
}

export default function TransferCases(): JSX.Element {
  const [rows, setRows] = useState<TransferCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await transferCasesService.list());
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Transfers from other accounts"
        subtitle="Money that came from an account that is not the client’s registered one. It is never credited."
      />
      {error && (
        <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>Nothing is waiting.</p>
      )}
      <ul className="space-y-3">
        {rows.map((r) => (
          <CaseCard key={r.id} item={r} onDone={() => { void reload(); }} />
        ))}
      </ul>
    </div>
  );
}
